import { Component, computed, inject, signal } from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { BottomSheetWrapperComponent } from '@app/components/bottom-sheet-wrapper/bottom-sheet-wrapper.component';
import { QRListStepperStageType } from '@app/modules/client-management/enums/qr-list-stepper-stage-type.enum';
import { ModalService } from '@app/services/modal/modal.service';
import { AppStore } from '@app/stores/app/app.store';
import { CompanyStore } from '@app/stores/company/company.store';
import { IDynamicActions } from '@app/types/dynamic-actions';
import { QrOnboardingStepperComponent } from '../../../../../components/qr-onboarding/qr-onboarding-stepper/qr-onboarding-stepper.component';
import { ReviewQrDetailsComponent } from '../../../../../components/qr-onboarding/review-qr-details/review-qr-details.component';
import { QrCodeListComponent } from '../qr-code-list/qr-code-list.component';

@Component({
    selector: 'app-active-qr-list-stepper',
    imports: [
        BottomSheetWrapperComponent,
        QrCodeListComponent,
        ReviewQrDetailsComponent,
        QrOnboardingStepperComponent,
    ],
    templateUrl: './qr-code-list-stepper.component.html',
    styleUrl: './qr-code-list-stepper.component.scss',
})
export class QrCodeListStepperComponent {
    private appStore = inject(AppStore);
    translate = this.appStore.i18n;
    private companyStore = inject(CompanyStore);
    readonly stepper = QRListStepperStageType;
    private modalService = inject(ModalService);
    readonly bottomSheetRef = inject(MatBottomSheetRef);

    public state = signal({
        step: QRListStepperStageType.ActiveQrList,
        qrHeaderSelectedIndex: -1,
    });

    step = computed(() => this.state().step);
    qrHeaderSelectedIndex = computed(() => this.state().qrHeaderSelectedIndex);
    id = computed(() => this.companyStore.id());

    headerTitle = computed(() => {
        const i18n = this.translate();
        const { TITLE__QrCodeOnboarding, TITLE__VendorSupplierOnboardingViaQRCode } =
            i18n;
        return this.step() === QRListStepperStageType.GenerateQR
            ? TITLE__VendorSupplierOnboardingViaQRCode
            : TITLE__QrCodeOnboarding;
    });

    titleAlignment = computed(() => {
        const { ReviewQR, GenerateQR } = QRListStepperStageType;
        if ([ReviewQR, GenerateQR].includes(this.step())) {
            return 'center';
        }
        return 'left';
    });

    bottomSheetClass = computed(() => {
        if (this.step() === QRListStepperStageType.ActiveQrList) {
            return {
                header: 'rcp-w-full rcp-mx-auto rcp-max-w-[1200px] rcp-font-poppins rcp-text-2xl',
                body: 'rcp-w-full rcp-mx-auto rcp-max-w-[1200px]',
            };
        }
        return undefined;
    });

    qrCodeDetailsActions = computed<IDynamicActions[]>(() => {
        const { ACTION__Back } = this.translate();
        return [
            {
                type: 'basic',
                text: ACTION__Back,
                testId: 'back_btn',
                disabled: false,
                isBusy: false,
                onClick: this.onBackToActiveQrList.bind(this),
            },
        ];
    });

    headerStepper = computed(() => {
        const qrHeaderSelectedIndex = this.qrHeaderSelectedIndex();
        const { ConfigureQR, QrCodeCreated } = this.translate();

        return {
            selectedIndex: qrHeaderSelectedIndex,
            completed: qrHeaderSelectedIndex === 2,
            items:
                this.step() === QRListStepperStageType.GenerateQR ||
                this.step() === QRListStepperStageType.ReviewQR
                    ? [ConfigureQR, QrCodeCreated]
                    : [],
        };
    });

    onQrIndexHeaderChange(index: number) {
        this.state.update((state) => ({
            ...state,
            qrHeaderSelectedIndex: index,
        }));
    }

    onBackToQrCodeList(e: Event) {
        e.preventDefault();
        this.state.update((state) => ({
            ...state,
            step: QRListStepperStageType.ActiveQrList,
        }));
    }

    onBackToActiveQrList() {
        this.state.update((state) => ({
            ...state,
            step: QRListStepperStageType.ActiveQrList,
        }));
    }

    onGoToGenerateQR() {
        this.companyStore.setQrDetails({
            value: {
                qrCodeId: '',
                name: '',
                qrLink: '',
                expirationDate: '',
            },
        });
        this.companyStore.setGenerateQRDetails({
            value: {
                id: '0',
                name: '',
                expirationDate: '',
                description: '',
                onboarding: 0,
                onBoardingOptions: '',
            },
        });

        this.state.update((state) => ({
            ...state,
            step: QRListStepperStageType.GenerateQR,
            qrHeaderSelectedIndex: 1,
        }));
    }

    onStepChange() {
        const stepCtr = this.step() + 1;
        const { GenerateQR } = QRListStepperStageType;
        this.state.update((state) => ({
            ...state,
            step: stepCtr,
            qrHeaderSelectedIndex: stepCtr === (GenerateQR as number) ? 1 : -1,
        }));
    }

    onClose(): void {
        this.bottomSheetRef.dismiss(this.step());
    }
}
